import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Dialog as ReactDialog,
  DialogActions,
  DialogContent,
  DialogProps as ReactDialogProps,
  DialogTitle,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DialogName } from 'app/enums';
import { modalsSelectorFactory } from 'app/store/modal/selectors';
import { modalsReducers } from 'app/store/modal/slice';
import React, { FC, PropsWithChildren, ReactElement } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { faXmark } from '@fortawesome/free-solid-svg-icons';

import { useStyles } from './Dialog.styles';

export interface DialogProps extends Omit<ReactDialogProps, 'open'> {
  name: DialogName;
  isCloseDisabled?: boolean;
  actions?: ReactElement | null;
  onClose?: (payload?: unknown) => void;
  allowBackdropClose?: boolean;
  contentHeight?: number;
}

export const Dialog: FC<PropsWithChildren<DialogProps>> = ({
  children,
  onClose,
  name,
  actions,
  allowBackdropClose = false,
  contentHeight = 500,
  ...rest
}) => {
  const theme = useTheme();
  const { classes } = useStyles({ contentHeight });

  const opened = useSelector(modalsSelectorFactory.useIsOpen(name));
  const fullScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const dispatch = useDispatch();

  const handleOnClose = (): void => {
    onClose?.();
    dispatch(modalsReducers.closeModal(name));
  };

  return (
    <ReactDialog
      fullScreen={fullScreen}
      className={classes.root}
      onClose={(e, reason) => {
        if (allowBackdropClose || reason !== 'backdropClick') {
          handleOnClose();
        }
      }}
      fullWidth
      open={opened}
      {...rest}
    >
      <DialogTitle className={classes.header}>
        <IconButton
          size="small"
          onClick={handleOnClose}
          className={classes.closeIcon}
          data-testid="IconButton"
        >
          <FontAwesomeIcon icon={faXmark} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers={false} className={classes.body}>
        {children}
      </DialogContent>
      <DialogActions className={classes.actions}>{actions}</DialogActions>
    </ReactDialog>
  );
};
